import * as React from 'react';

import { Checkbox as CheckboxPrimitive } from '@base-ui-components/react/checkbox';
import {
  type CheckboxGroupChangeEventDetails,
  CheckboxGroup as CheckboxGroupPrimitive,
} from '@base-ui-components/react/checkbox-group';
import { XIcon } from 'lucide-react';

import { cn } from 'shared/lib/utils';

export type ButtonSelectOption<T> = {
  value: T;
  label: React.ReactNode;
};

type ButtonSelectColor = 'primary' | 'alternate' | 'neutral';
type ButtonSelectSize = 'default' | 'compact';
type ButtonSelectSelectionMode = 'multiple' | 'single';

type ButtonSelectProps<T> = {
  options: ButtonSelectOption<T>[];
  values: T[];
  onValuesChange: (
    next: T[],
    eventDetails: CheckboxGroupChangeEventDetails
  ) => void;
  color?: ButtonSelectColor;
  size?: ButtonSelectSize;
  selectionMode?: ButtonSelectSelectionMode;
  hideUnselected?: boolean;
  className?: string;
  'aria-label'?: string;
};

const checkedClasses: Record<ButtonSelectColor, string> = {
  primary: 'bg-primary text-white hover:bg-primary60 active:bg-primary80',
  alternate: 'bg-accent text-white hover:bg-accent60 active:bg-accent80',
  neutral: 'bg-body-copy-gray text-white hover:bg-black active:bg-black',
};

const uncheckedClasses: Record<ButtonSelectColor, string> = {
  primary: 'bg-primary-alpha15 text-primary hover:bg-primary-alpha30',
  alternate: 'bg-accent-alpha15 text-accent hover:bg-accent-alpha30',
  neutral:
    'bg-neutral text-body-copy-gray hover:bg-gray-line active:bg-body-copy-gray active:text-white',
};

const sizeClasses: Record<ButtonSelectSize, string> = {
  default: 'px-5 py-3 text-sm',
  compact: 'px-4 py-2.5 text-sm',
};

const ButtonSelect = <T,>({
  options,
  values,
  onValuesChange,
  color = 'primary',
  size = 'default',
  selectionMode = 'multiple',
  hideUnselected = false,
  className = '',
  'aria-label': ariaLabel,
}: ButtonSelectProps<T>) => {
  const indexedOptions = options.map((option, index) => ({
    option,
    id: String(index),
  }));
  const checkedIds = values
    .map((value) => options.findIndex((option) => option.value === value))
    .filter((index) => index >= 0)
    .map(String);
  const collapsed =
    selectionMode === 'single' && hideUnselected && checkedIds.length > 0;
  const visibleOptions = collapsed
    ? indexedOptions.filter(({ id }) => id === checkedIds[0])
    : indexedOptions;
  const toValues = (ids: string[]) =>
    ids.map((id) => options[Number(id)].value);

  // TODO: render a RadioGroup/Radio primitive for 'single' and
  // get rid of this hack
  const handleValueChange = (
    nextIds: string[],
    event: CheckboxGroupChangeEventDetails
  ) => {
    if (selectionMode === 'multiple') {
      onValuesChange(toValues(nextIds), event);
      return;
    }
    const added = nextIds.find((id) => !checkedIds.includes(id));
    if (added !== undefined) {
      onValuesChange(toValues([added]), event);
    } else if (hideUnselected) {
      onValuesChange([], event);
    }
  };

  return (
    <CheckboxGroupPrimitive
      data-slot="button-select"
      value={checkedIds}
      onValueChange={handleValueChange}
      aria-label={ariaLabel}
      className={cn('flex flex-wrap gap-3', className)}
    >
      {visibleOptions.map(({ option, id }) => (
        <CheckboxPrimitive.Root
          key={id}
          name={id}
          nativeButton
          render={(props, state) => (
            <button
              type="button"
              {...props}
              className={cn(
                'inline-flex cursor-pointer items-center gap-2 rounded-lg font-medium tracking-wide transition-colors outline-none [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:stroke-[2.5]',
                sizeClasses[size],
                'focus-visible:ring-[3px] focus-visible:ring-ring/50',
                state.checked ? checkedClasses[color] : uncheckedClasses[color]
              )}
            >
              {option.label}
              {collapsed && <XIcon aria-hidden="true" />}
            </button>
          )}
        />
      ))}
    </CheckboxGroupPrimitive>
  );
};

export { ButtonSelect };
